/**
 * OpenDealPicker — the Log Activity sheet's search over OPEN deals (slice B,
 * RULED 2026-10-08, R1). GET /v1/sales/deals has no open/closed filter, so the
 * search asks each OPEN stage once (the Reports open drill's shape) and merges
 * the four answers by id. It never asks a closed stage, and an empty box asks
 * nothing. The §35 DealPicker is NOT reused: it searches closed deals too.
 *
 * Pick-only: the sheet keeps the picked deal as an option of its own Deal
 * <select>, next to "No deal" and the contact's account's open deals.
 */
import { fetchDeals } from '../../api'
import type { SalesDeal } from '../../types'
import { EntityPicker } from './EntityPicker'
import { OPEN_DEAL_STAGES, mergeOpenDeals } from './logActivitySheet'
import { laneLabel } from './pipelineView'

/** Rows per stage — a picker asks for a handful, as DealPicker does. */
const PER_STAGE = 8

async function searchOpenDeals(q: string): Promise<SalesDeal[]> {
  const search = q.trim()
  if (!search) return []
  const lists = await Promise.all(OPEN_DEAL_STAGES.map(stage => fetchDeals({ stage, search, limit: PER_STAGE })))
  return mergeOpenDeals(lists)
}

export function OpenDealPicker({ id, className, text, onTextChange, onPick }: {
  id: string
  className: string
  text: string
  onTextChange: (t: string) => void
  onPick: (deal: SalesDeal) => void
}) {
  return (
    <EntityPicker<SalesDeal>
      id={id} className={className}
      placeholder="Search other open deals by title or company…"
      text={text} onTextChange={onTextChange}
      search={searchOpenDeals}
      rowKey={d => d.id}
      renderRow={d => (
        <>
          <span className="text-13 font-medium text-ink-primary truncate">{d.name}</span>
          <span className="text-11 text-ink-muted whitespace-nowrap truncate">
            {d.company !== '' ? `${d.company} · ${laneLabel(d.stage)}` : laneLabel(d.stage)}
          </span>
        </>
      )}
      onPick={onPick}
      emptyPrompt="Type to search open deals"
      noMatch="No open deal matches that search"
    />
  )
}
